import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Skeleton from '@material-ui/lab/Skeleton'
import {
  ATTENTION_ROW_HEIGHT,
  ATTENTION_ROW_HEIGHT_COMPACT,
} from './attention-row-size'
import useStyles from './styles'

export const ATTENTION_SKELETON_ROWS = 3
export const UPCOMING_SKELETON_ROWS = 4

// The skeleton rows reuse the real row classes so they take the same space;
// this only turns off the row hover and pointer.
const useSkeletonStyles = makeStyles({
  row: {
    pointerEvents: 'none',
  },
  tabs: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    height: 48,
    borderBottom: '1px solid #E0E0E0',
  },
})

export const AttentionListSkeleton = ({
  compact = false,
  rows = ATTENTION_SKELETON_ROWS,
}) => {
  const classes = useStyles()
  const skeletonClasses = useSkeletonStyles()
  const avatarSize = compact ? 40 : 48

  return (
    <Box
      className={classes.list}
      style={{
        minHeight:
          rows *
          (compact ? ATTENTION_ROW_HEIGHT_COMPACT : ATTENTION_ROW_HEIGHT),
      }}
      data-testid="needs-attention-loading"
    >
      {Array.from({ length: rows }, (_, index) => (
        <Box
          key={index}
          className={`${classes.row} ${skeletonClasses.row} ${
            classes.attentionRow
          } ${compact ? classes.attentionRowCompact : ''}`}
        >
          <Skeleton variant="circle" width={avatarSize} height={avatarSize} />
          <Box className={classes.rowMain}>
            <Skeleton variant="text" width={140} height={20} />
            <Skeleton variant="text" width="70%" height={18} />
            <Skeleton variant="text" width={110} height={18} />
          </Box>
          <Box className={classes.rowTrailing}>
            <Skeleton
              variant="rect"
              width={compact ? 40 : 96}
              height={compact ? 40 : 36}
              style={{ borderRadius: compact ? '50%' : 20 }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  )
}

AttentionListSkeleton.propTypes = {
  compact: PropTypes.bool,
  rows: PropTypes.number,
}

export const AttentionTabsSkeleton = ({ compact = false }) => {
  const classes = useStyles()
  const skeletonClasses = useSkeletonStyles()

  return (
    <Box
      className={`${classes.tabs} ${compact ? classes.tabsCompact : ''} ${
        skeletonClasses.tabs
      }`}
      data-testid="needs-attention-tabs-loading"
    >
      <Skeleton variant="text" width={56} height={20} />
      <Skeleton variant="text" width={64} height={20} />
      <Skeleton variant="text" width={88} height={20} />
    </Box>
  )
}

AttentionTabsSkeleton.propTypes = { compact: PropTypes.bool }

export const UpcomingListSkeleton = ({ rows = UPCOMING_SKELETON_ROWS }) => {
  const classes = useStyles()
  const skeletonClasses = useSkeletonStyles()

  return (
    <Box className={classes.list} data-testid="upcoming-lessons-loading">
      {Array.from({ length: rows }, (_, index) => (
        <Box
          key={index}
          className={`${classes.row} ${skeletonClasses.row} ${classes.upcomingRow}`}
        >
          <Box className={classes.rowMain}>
            <Skeleton variant="text" width={160} height={20} />
            <Skeleton variant="text" width={110} height={18} />
          </Box>
          <Box className={classes.rowTrailing}>
            <Box className={classes.rowSchedule}>
              <Skeleton
                variant="text"
                width={64}
                height={18}
                style={{ marginLeft: 'auto' }}
              />
              <Skeleton
                variant="text"
                width={48}
                height={18}
                style={{ marginLeft: 'auto' }}
              />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

UpcomingListSkeleton.propTypes = { rows: PropTypes.number }

const CardHeaderSkeleton = ({ actionWidth }) => {
  const classes = useStyles()

  return (
    <Box className={`${classes.cardHeader} ${classes.cardHeaderRoomy}`}>
      <Box className={classes.cardHeaderLeft}>
        <Skeleton variant="circle" width={40} height={40} />
        <Box className={classes.cardHeading}>
          <Skeleton variant="text" width={150} height={21} />
          <Skeleton variant="text" width={190} height={18} />
        </Box>
      </Box>
      <Skeleton variant="text" width={actionWidth} height={20} />
    </Box>
  )
}

CardHeaderSkeleton.propTypes = { actionWidth: PropTypes.number.isRequired }

// Page-level placeholder for the "Manage your coaching business" section.
const ManageBusinessSkeleton = () => {
  const classes = useStyles()

  return (
    <Box className={classes.section} data-testid="manage-business-skeleton">
      <Skeleton
        variant="text"
        width={240}
        height={23}
        className={classes.sectionTitle}
      />
      <Box className={classes.grid}>
        <Box className={classes.card}>
          <CardHeaderSkeleton actionWidth={72} />
          <AttentionTabsSkeleton />
          <AttentionListSkeleton />
        </Box>
        <Box className={classes.card}>
          <CardHeaderSkeleton actionWidth={96} />
          <UpcomingListSkeleton />
        </Box>
      </Box>
    </Box>
  )
}

export default ManageBusinessSkeleton
